"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableHead = void 0;
var tslib_1 = require("tslib");
var react_1 = tslib_1.__importStar(require("react"));
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var text_1 = require("../text");
var TableBox_1 = require("./TableBox");
var use_middleware_1 = require("./util/use-middleware");
var is_callable_1 = require("../_util/is-callable");
var config_context_1 = require("../_util/config-context");
var TableBody_1 = require("./TableBody");
var TableContext_1 = require("./TableContext");
var DraggableTable_1 = require("./addons/draggable/DraggableTable");
var ResizableTable_1 = require("./addons/resizable/ResizableTable");
var get_row_exact_width_1 = require("./util/get-row-exact-width");
exports.TableHead = react_1.forwardRef(function TableHead(props, ref) {
    var classPrefix = config_context_1.useConfig().classPrefix;
    var _a = react_1.useContext(TableContext_1.TableContext), scrollStatus = _a.scrollStatus, columnsWidths = _a.columnsWidths;
    var _b = react_1.useContext(DraggableTable_1.DragContext), dragEventHandlers = _b.dragEventHandlers, draggableColumns = _b.draggableColumns;
    var isResizing = react_1.useContext(ResizableTable_1.ResizeContext).isResizing;
    var columns = props.columns, records = props.records, addons = props.addons, tableBoxStyle = props.tableBoxStyle, disableTextOverflow = props.disableTextOverflow, hideHeader = props.hideHeader, virtualizedOptions = props.virtualizedOptions, headerRows = props.headerRows, columnsDraggable = props.columnsDraggable, columnsResizable = props.columnsResizable, onResize = props.onResize;
    var hasFixedColumn = columns.find(function (column) { return !!column.fixed; });
    var renderColumn = use_middleware_1.useMiddleware(addons, "onInjectColumn")(function (record, rowKey, recordIndex, column, columnIndex) {
        var key = column.key, header = column.header;
        var children = header;
        // pass a render function
        if (is_callable_1.isCallable(header)) {
            children = header(column);
        }
        // forgot to pass, use a key as a reminder
        if (header === undefined) {
            children = key;
        }
        // plain values
        if (typeof children === "string" || typeof children === "number") {
            children = (react_1.default.createElement(text_1.Text, { overflow: !disableTextOverflow, title: disableTextOverflow ? undefined : String(children) }, children));
        }
        return {
            props: {},
            children: children,
        };
    });
    var tableHeaders = columns.map(function (column, index) {
        var _a, _b;
        var _c = renderColumn(null, null, -1, column, index), props = _c.props, children = _c.children;
        var cKey = column.key;
        if (props.colSpan === 0) {
            return null;
        }
        if (virtualizedOptions) {
            props.style = tslib_1.__assign(tslib_1.__assign({}, (props.style || {})), { display: "inline-block", overflow: "hidden", boxSizing: "border-box", 
                // 全部列都有宽度时要自适应
                flexGrow: column.width && !get_row_exact_width_1.getRowExactWidth(columns) ? undefined : 1, flexBasis: column.width || 1 });
            if (virtualizedOptions.verticalMiddle) {
                props.style.display = "inline-flex";
                props.style.alignItems = "center";
            }
        }
        // 固定列
        if ((column.fixed === "left" || column.fixed === "right") &&
            column.fixed !== scrollStatus &&
            scrollStatus !== "no-scroll") {
            var end = TableBody_1.getFixedEndColumn(columns);
            props.className = classnames_1.default(props.className, classPrefix + "-table__td--fixed", (_a = {},
                _a[classPrefix + "-table__td--fixed-" + column.fixed] = end[column.fixed] === index,
                _a));
            props.style = tslib_1.__assign(tslib_1.__assign({}, (props.style || {})), (_b = { position: "sticky" }, _b[column.fixed] = TableBody_1.getFixedOffset(columns, index, columnsWidths, column.fixed), _b));
        }
        // 列拖拽排序
        if (!column.fixed && columnsDraggable && !("ignore" in column)) {
            var onDragEnter = dragEventHandlers.onDragEnter, onDragOver = dragEventHandlers.onDragOver, onDrop = dragEventHandlers.onDrop, onDragStart = dragEventHandlers.onDragStart, onDrag = dragEventHandlers.onDrag, onDragLeave = dragEventHandlers.onDragLeave, onDragEnd = dragEventHandlers.onDragEnd, onMouseEnter = dragEventHandlers.onMouseEnter, onMouseLeave = dragEventHandlers.onMouseLeave;
            props["data-column"] = column.key;
            props.onDragEnter = onDragEnter;
            props.onDragOver = onDragOver;
            props.onDrop = onDrop;
            if (draggableColumns === "all" || draggableColumns.includes(cKey)) {
                props.draggable = true;
                props.onDragStart = onDragStart;
                props.onDrag = onDrag;
                props.onDragLeave = onDragLeave;
                props.onDragEnd = onDragEnd;
                props.onMouseEnter = onMouseEnter;
                props.onMouseLeave = onMouseLeave;
                props.className = classnames_1.default(props.className, "is-draggable");
            }
        }
        var childrenStyle = columnsResizable && isResizing ? { pointerEvents: "none" } : {};
        var cell = (react_1.default.createElement(text_1.Text, tslib_1.__assign({ key: cKey, parent: "th" }, props, { align: column.align }),
            react_1.default.createElement("div", { style: childrenStyle }, children)));
        // 无数据时通过头确定列宽
        if ((records || []).length === 0 && hasFixedColumn) {
            return (react_1.default.createElement(TableBody_1.CellResizeObserver, { key: "" + cKey + index, onResize: function (width) {
                    onResize(function (widths) {
                        var columnsWidths = tslib_1.__spread(widths);
                        columnsWidths[index] = width;
                        return columnsWidths;
                    });
                } }, cell));
        }
        return cell;
    });
    var renderGroupingHeader = react_1.useCallback(function (heads) {
        return headerRows.map(function (row) { return (react_1.default.createElement("tr", { key: row.map(function (i) { return i.key; }).join("-") }, row.map(function (_a) {
            var key = _a.key, isLeaf = _a.isLeaf, title = _a.title, _b = _a.align, align = _b === void 0 ? "left" : _b, colSpan = _a.colSpan;
            if (isLeaf) {
                var index = columns.findIndex(function (i) { return i.key === key; });
                return heads[index];
            }
            return (react_1.default.createElement(text_1.Text, { key: key, parent: "th", colSpan: colSpan, align: align },
                react_1.default.createElement("div", null, title)));
        }))); });
    }, [columns, headerRows]);
    return use_middleware_1.useMiddleware(addons, "onInjectHead")(function (props) {
        // tableBoxStyle 可能包含横向滚动的 minWidth，如果有则覆盖计算的最小宽度
        var minWidth = virtualizedOptions
            ? (tableBoxStyle === null || tableBoxStyle === void 0 ? void 0 : tableBoxStyle.minWidth) || get_row_exact_width_1.getRowExactWidth(props.columns) || undefined
            : tableBoxStyle === null || tableBoxStyle === void 0 ? void 0 : tableBoxStyle.minWidth;
        return (react_1.default.createElement("div", { className: classPrefix + "-table__header", ref: ref, style: hideHeader ? { display: "none" } : undefined },
            react_1.default.createElement(TableBox_1.TableBox, { columns: columns, style: tslib_1.__assign(tslib_1.__assign({}, (tableBoxStyle || {})), { display: virtualizedOptions ? "block" : undefined, minWidth: minWidth }), classPrefix: classPrefix },
                react_1.default.createElement("thead", { style: virtualizedOptions ? { display: "block" } : undefined }, headerRows ? (renderGroupingHeader(tableHeaders)) : (react_1.default.createElement("tr", { style: virtualizedOptions ? { display: "flex" } : undefined }, tableHeaders))))));
    })(props);
});
exports.TableHead.displayName = "TableHead";
//# sourceMappingURL=TableHead.js.map